// Loads the entries for the spreadsheet with their FULL field data.
//
// The list endpoint only returns full fields when the query does not "involve fields" for more
// than one content type (see EntryApi.loadEntries / the CMA "A Content Type ID is required"
// error). So:
//   - single content type  -> ask loadEntries for all fields directly (one request)
//   - multi type / all      -> list the matching ids, then bulk-fetch full entries by id, which
//                              returns all fields for any type (filtering by sys.id does not
//                              involve fields). Original order is preserved.

import { loadEntries, httpGet } from '../../EntryApi'

const ID_CHUNK = 100

async function fetchFullByIds(endpoint: string, ids: string[]): Promise<any[]> {
	const out: any[] = []
	for (let i = 0; i < ids.length; i += ID_CHUNK) {
		const chunk = ids.slice(i, i + ID_CHUNK)
		const res = await httpGet(endpoint + '/entries', {
			'sys.id[in]': chunk.join(','),
			limit: chunk.length,
		})
		out.push(...(res?.items ?? []))
	}
	return out
}

export async function loadSpreadsheetEntries(
	endpoint: string,
	filter: any,
	limit: number,
	skip: number,
): Promise<{ items: any[], total: number }> {
	// single content type: loadEntries honours allFields and returns full field data in one call
	if (filter?.contentType && !filter?.contentTypes?.length) {
		return await loadEntries(endpoint, { ...filter, allFields: true }, limit, skip)
	}

	// multi-type / all: page the ids, then bulk-fetch full entries and restore the paged order
	const list = await loadEntries(endpoint, filter, limit, skip)
	const ids = list.items.map((i: any) => i.sys.id)
	if (!ids.length) return { items: [], total: list.total }

	const full = await fetchFullByIds(endpoint, ids)
	const byId: Record<string, any> = {}
	for (const entry of full) byId[entry.sys.id] = entry
	const items = ids.map(id => byId[id]).filter(Boolean)
	return { items, total: list.total }
}
